'use client';
import { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import type { GalleryData } from './AccessForm';

interface PrivateGalleryProps {
  galleryData: GalleryData;
  goBack: () => void;
}

export default function PrivateGallery({ galleryData, goBack }: PrivateGalleryProps) {
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownload = (imageUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    link.click();
  };

  const sanitizeFileName = (value: string) => {
    return value.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').trim() || 'photo';
  };

  const getFileExtensionFromUrl = (url: string) => {
    try {
      const cleanUrl = url.split('?')[0];
      const extension = cleanUrl.split('.').pop()?.toLowerCase();

      if (extension && extension.length <= 5) {
        return extension === 'jpeg' ? 'jpg' : extension;
      }
    } catch {
      // ignore and fall back below
    }

    return 'jpg';
  };

  const handleDownloadAll = async () => {
    try {
      if (!galleryData.photos?.length) {
        alert('No photos available to download.');
        return;
      }

      setIsDownloadingAll(true);

      const zip = new JSZip();
      const folderName = sanitizeFileName(galleryData.name || 'gallery');
      const zipFolder = zip.folder(folderName);

      if (!zipFolder) {
        throw new Error('Failed to create ZIP folder.');
      }

      await Promise.all(
        galleryData.photos.map(async (photo, index) => {
          const imageUrl = photo.image;

          if (!imageUrl) return;

          // Fetch straight from Azure (CORS allows it) — proxying through our API would hit
          // Vercel's 4.5 MB function response limit for full-size photos.
          const response = await fetch(imageUrl);

          if (!response.ok) {
            throw new Error(`Failed to fetch image ${photo.title || index + 1}`);
          }

          const blob = await response.blob();
          const extension =
            blob.type?.split('/')[1]?.replace('jpeg', 'jpg') || getFileExtensionFromUrl(imageUrl);

          const baseName = sanitizeFileName(photo.title || `photo-${index + 1}`);
          const fileName = `${baseName}.${extension}`;

          zipFolder.file(fileName, blob);
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${folderName}.zip`);
    } catch (error) {
      console.error('Download all failed:', error);
      alert('Something went wrong while preparing the gallery download.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Gallery Header */}
      <div className="glass-panel rounded-2xl p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">{galleryData.name}</h2>
            {galleryData.description && (
              <p className="text-muted-foreground mb-4">{galleryData.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Icon name="PhotoIcon" size={16} />
                {galleryData.photoCount} photos
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadAll}
              disabled={isDownloadingAll}
              className="px-6 py-3 bg-white text-background rounded-lg font-medium hover:bg-white/90 transition-all duration-300 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Icon name="ArrowDownTrayIcon" size={20} />
              {isDownloadingAll ? 'Preparing ZIP...' : 'Download All'}
            </button>
            <button
              onClick={goBack}
              className="px-6 py-3 border border-white/20 rounded-lg font-medium text-white hover:bg-white/10 transition-all duration-300 flex items-center gap-2"
            >
              <Icon name="ArrowLeftOnRectangleIcon" size={20} />
              Go Back
            </button>
          </div>
        </div>
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {galleryData.photos.map((photo) => (
          <div key={photo.id} className="group relative overflow-hidden rounded-2xl aspect-square">
            <AppImage
              src={photo.image}
              alt={photo.alt}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Download Button */}
            <button
              onClick={() =>
                handleDownload(
                  photo.image,
                  `${sanitizeFileName(photo.title)}.${getFileExtensionFromUrl(photo.image)}`
                )
              }
              className="absolute top-4 right-4 w-10 h-10 rounded-full glass-panel flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white/20"
              aria-label={`Download ${photo.title}`}
            >
              <Icon name="ArrowDownTrayIcon" size={20} />
            </button>

            {/* Photo Title */}
            <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <h3 className="text-lg font-bold text-white">{photo.title}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Gallery Info */}
      <div className="glass-panel rounded-2xl p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Need high-resolution files or prints?{' '}
          <a href="mailto:ryansukeena@gmail.com" className="text-white hover:underline">
            Contact us
          </a>
        </p>
      </div>
    </div>
  );
}
